import { InterpreterOptions, MachineOptions } from 'xstate'
import { UseMachineOptions } from '@xstate/react/lib/useMachine'
import { ModalProps } from 'almanac-ui/Modal/Types'
import {
  BrandPlanCrosspost,
  BrandPlanStrategyType,
  BrandPlanTactic,
  BrandPlanTacticContents,
  CrosspostFrequency,
  DailyFrequency,
  MediaObject,
  Source,
  Theme,
  Website,
  WeekDay,
  AnchorContentType,
  BatchCampaignPreparation,
} from 'types'

export enum EventId {
  Back = 'Back',
  PrepareCampaign = 'PrepareCampaign',
  Continue = 'Continue',
  Error = 'Error',
  PreparationComplete = 'PreparationComplete',
  ReviewComplete = 'ReviewComplete',
  Finish = 'Finish',
  AnchorContentUploaded = 'AnchorContentUploaded',
  SourceUploaded = 'SourceUploaded',
}

export enum StateId {
  CreateCampaign = 'CreateCampaign',
  Analysing = 'Analysing',
  AnalysisError = 'AnalysisError',
  Review = 'Review',
  Distribution = 'Distribution',
}

export interface StateMeta {
  title: string
}

export interface Context {
  start_date: string
  topic: string
  length: number
  week_days: WeekDay[]
  goal: string
  audience: string
  daily_frequency: DailyFrequency
  days_before_review: number
  tactics: BrandPlanTactic[]
  instagram_contents: BrandPlanTacticContents
  facebook_contents: BrandPlanTacticContents
  linkedin_contents: BrandPlanTacticContents
  twitter_contents: BrandPlanTacticContents
  email_contents: BrandPlanTacticContents
  seo_contents: BrandPlanTacticContents
  tiktok_contents: BrandPlanTacticContents
  youtube_contents: BrandPlanTacticContents
  video_crosspost_contents: BrandPlanTacticContents
  crosspost_contents: BrandPlanTacticContents
  google_my_business_contents: BrandPlanTacticContents
  crosspost_frequency: CrosspostFrequency
  cover_image: string
  error?: Error
  anchor_content?: (Website | MediaObject) & {
    type: AnchorContentType
  }
  sources: Array<Source>
  strategy: {
    type: BrandPlanStrategyType.Crosspost
    call_to_action: string
    theme: string
    themes: Theme[]
    target_link: string
    anchor_content_type?: string
    anchor_content_id?: number
  }
  transient: {
    file?: File
    abortController: AbortController
  }
}

export interface AnchorContentUploadedEvent {
  type: EventId.AnchorContentUploaded
  anchorContent: (Website | MediaObject) & {
    type: AnchorContentType
  }
}

export interface SourceUploadedEvent {
  type: EventId.SourceUploaded
  source: Source
}

export interface BackEvent {
  type: EventId.Back
}

export interface ContinueEvent {
  type: EventId.Continue
}

export interface FinishEvent {
  type: EventId.Finish
  instagram_contents: BrandPlanTacticContents
  facebook_contents: BrandPlanTacticContents
  linkedin_contents: BrandPlanTacticContents
  twitter_contents: BrandPlanTacticContents
  email_contents: BrandPlanTacticContents
  seo_contents: BrandPlanTacticContents
  tiktok_contents: BrandPlanTacticContents
  youtube_contents: BrandPlanTacticContents
  video_crosspost_contents: BrandPlanTacticContents
  crosspost_contents: BrandPlanTacticContents
  google_my_business_contents: BrandPlanTacticContents
  crosspost_frequency: CrosspostFrequency
  tactics: BrandPlanTactic[]
}

export interface ReviewCompleteEvent {
  type: EventId.ReviewComplete
  theme: string
  description: string
  audience: string
  goal: string
  callToAction: string
  targetLink: string
}

export interface BasePrepareCampaignEvent {
  type: EventId.PrepareCampaign
  topic: string
  startDate: string
  length: number
  targetLink: string
  sources: Source[]
  anchorContent?: (Website | MediaObject) & {
    type: AnchorContentType
  }
  file?: File
}

export interface PrepareCampaignEventWithFile extends BasePrepareCampaignEvent {
  file: File
}

export type PrepareCampaignEvent = PrepareCampaignEventWithFile | BasePrepareCampaignEvent

export interface PreparationCompleteEvent {
  type: EventId.PreparationComplete
  preparation: BatchCampaignPreparation
}

export interface ErrorEvent {
  type: EventId.Error
  error: Error
}

export type Event =
  | BackEvent
  | PrepareCampaignEvent
  | ErrorEvent
  | PreparationCompleteEvent
  | ReviewCompleteEvent
  | ContinueEvent
  | FinishEvent
  | AnchorContentUploadedEvent
  | SourceUploadedEvent

export type State =
  | {
      value: StateId.CreateCampaign
      context: Context
    }
  | {
      value: StateId.Analysing
      context: Context
    }
  | {
      value: StateId.AnalysisError
      context: Context
    }
  | {
      value: StateId.Review
      context: Context
    }
  | {
      value: StateId.Distribution
      context: Context
    }

export interface Props {
  brandPlanId: number
}

export type Options = Partial<InterpreterOptions> &
  Partial<UseMachineOptions<Context, Event>> &
  Partial<MachineOptions<Context, Event>>

export interface AddCampaignModalProps extends ModalProps {
  startDate: string
  brandPlan: BrandPlanCrosspost
}
